import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewEncapsulation, inject, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {NavigationMenuService} from '@app/services/navigation-menu.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {ResolvedBookmark, ResolvedNavNode} from '../model/nav-tree';
import { NgTemplateOutlet, AsyncPipe, UpperCasePipe } from '@angular/common';

const FAVORITES = '__favorites';
const COMPACT = '__menu';
const OPEN_DELAY = 150;
const CLOSE_DELAY = 300;

/**
 * The top menu, from the tree the server sent: one list of top-level entries, their groups as
 * dropdowns - or, when they no longer fit beside the project switcher, all of them folded into
 * a single MENU dropdown. Nested groups render as headings inside a dropdown rather than as
 * flyouts, so every menu scrolls on a short screen.
 *
 * A dropdown opens on a short hover, and a click pins it; Esc, a click elsewhere or a
 * navigation closes it. Items carry a star, as in the side menu.
 */
@Component({
  selector: 'app-nav-top-menu',
  templateUrl: './nav-top-menu.component.html',
  styleUrls: ['./nav-top-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, RouterLink, AsyncPipe, UpperCasePipe],
  host: {
    '(document:keydown.escape)': 'onEscape()',
    '(document:click)': 'onDocumentClick($event)',
  }
})
export class NavTopMenuComponent implements AfterViewInit, OnDestroy {
  private menu = inject(NavigationMenuService);
  private navigationState = inject(NavigationStateService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  readonly FAVORITES = FAVORITES;
  readonly COMPACT = COMPACT;
  readonly main$ = this.menu.main$;
  readonly favorites$ = this.menu.favorites$;
  readonly activeIds$ = this.menu.activeIds$;
  readonly starredKeys$ = this.menu.starredKeys$;

  openId: string | null = null;
  compact = false;
  /** The favorite being renamed in place, by key. */
  renaming: string | null = null;

  readonly measure = viewChild<ElementRef<HTMLElement>>('measure');

  private pinned = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver?: ResizeObserver;
  private frame: number | null = null;
  private readonly destroyed = new Subject<void>();

  constructor() {
    const router = inject(Router);

    router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroyed))
      .subscribe(() => this.closeAll());
    this.main$.pipe(takeUntil(this.destroyed)).subscribe(() => this.scheduleMeasure());
    this.favorites$.pipe(takeUntil(this.destroyed)).subscribe(() => this.scheduleMeasure());
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.host.nativeElement);
      const measure = this.measure();
      if (measure) {
        this.resizeObserver.observe(measure.nativeElement);
      }
    }
    this.scheduleMeasure();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.resizeObserver?.disconnect();
    this.clearTimers();
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }
  }

  trackById(_index: number, node: {id?: string; key?: string}): string {
    return node.id || node.key;
  }

  isStarred(node: ResolvedNavNode, keys: Set<string>): boolean {
    const key = this.menu.starKey(node);
    return !!key && keys.has(key);
  }

  toggleStar(node: ResolvedNavNode, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.menu.toggleStar(node);
  }

  removeFavorite(favorite: ResolvedBookmark, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.navigationState.removeBookmark(favorite.key);
  }

  startRename(favorite: ResolvedBookmark, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.renaming = favorite.key;
    this.pinned = true;
    setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>('.sn-top-dropdown__rename')?.select());
  }

  finishRename(favorite: ResolvedBookmark, label: string): void {
    if (this.renaming !== favorite.key) {
      return;
    }
    this.renaming = null;
    if (label.trim() !== favorite.label) {
      this.navigationState.renameBookmark(favorite.key, label);
    }
  }

  cancelRename(event: Event): void {
    event.stopPropagation();
    this.renaming = null;
  }

  moveFavorite(favorite: ResolvedBookmark, delta: -1 | 1, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.navigationState.moveBookmark(favorite.key, delta);
  }

  hoverOpen(id: string): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
    this.closeTimer = null;
    if (this.openId === id || this.pinned) {
      return;
    }
    if (this.openTimer) {
      clearTimeout(this.openTimer);
    }
    this.openTimer = setTimeout(() => {
      this.openId = id;
      this.cdr.markForCheck();
    }, this.openId ? 0 : OPEN_DELAY);
  }

  hoverClose(id: string): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
    }
    this.openTimer = null;
    if (this.pinned || this.openId !== id) {
      return;
    }
    this.closeTimer = setTimeout(() => {
      this.openId = null;
      this.cdr.markForCheck();
    }, CLOSE_DELAY);
  }

  /** A click pins a menu open - or closes the one it pinned. */
  toggle(id: string): void {
    this.clearTimers();
    if (this.openId === id && this.pinned) {
      this.closeAll();
      return;
    }
    this.openId = id;
    this.pinned = true;
  }

  onToggleKeydown(event: KeyboardEvent, id: string): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openId = id;
      this.pinned = true;
      setTimeout(() => this.focusFirstItem(id));
    }
  }

  closeAll(): void {
    this.clearTimers();
    this.openId = null;
    this.pinned = false;
    this.cdr.markForCheck();
  }

  onEscape(): void {
    if (!this.openId) {
      return;
    }
    const id = this.openId;
    this.closeAll();
    const toggle = this.host.nativeElement.querySelector<HTMLElement>(`[data-sn-toggle="${id}"]`);
    toggle?.focus();
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.openId && !this.host.nativeElement.contains(event.target as Node)) {
      this.closeAll();
    }
  }

  private focusFirstItem(id: string): void {
    const menu = this.host.nativeElement.querySelector(`[data-sn-menu="${id}"]`);
    menu?.querySelector<HTMLElement>('a, button')?.focus();
  }

  private clearTimers(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
    this.openTimer = null;
    this.closeTimer = null;
  }

  private scheduleMeasure(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.measureOverflow();
    });
  }

  /**
   * Fold everything into MENU when the entries' natural width no longer fits. Measured on an
   * off-screen copy that never changes with the result, so it cannot flip back and forth.
   */
  private measureOverflow(): void {
    const measure = this.measure()?.nativeElement;
    if (!measure) {
      return;
    }
    const host = this.host.nativeElement;
    const switcher = host.querySelector<HTMLElement>('.projects-dropdown');
    const available = host.clientWidth - (switcher ? switcher.offsetWidth : 0) - 24;
    let needed = 0;
    measure.querySelectorAll<HTMLElement>('.nav-overflow-measure__item').forEach((item) => needed += item.offsetWidth);
    const compact = needed > 0 && needed > available;
    if (compact !== this.compact) {
      this.compact = compact;
      this.openId = null;
      this.pinned = false;
      this.cdr.markForCheck();
    }
  }
}
